import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface DashboardData {
  // Statystyki główne
  stats: {
    totalRevenue: number;
    revenueChange: number;
    totalOffers: number;
    offersChange: number;
    totalClients: number;
    clientsChange: number;
    conversionRate: number;
    conversionChange: number;
    averageOrderValue: number;
    totalProducts: number;
  };
  
  // Dane do wykresów
  revenueData: Array<{
    date: string;
    revenue: number;
    offers: number;
    accepted: number;
  }>;
  
  productData: Array<{
    name: string;
    value: number;
    count: number;
    percentage: number;
  }>;
  
  statusData: Array<{
    name: string;
    value: number;
    color: string;
    percentage: number;
  }>;
  
  // Top klienci
  topClients: Array<{
    id: string;
    name: string;
    revenue: number;
    ordersCount: number;
    lastOrderDate: string;
    growth: number;
  }>;
  
  // Ostatnie aktywności
  recentActivities: Array<{
    id: string;
    type: 'offer_created' | 'offer_accepted' | 'offer_rejected' | 'client_added' | 'offer_viewed';
    description: string;
    timestamp: string;
    icon: string;
    color: string;
  }>;
  
  // Wydajność sprzedawców
  salesPerformance: Array<{
    salesperson: string;
    revenue: number;
    offersCount: number;
    conversionRate: number;
    avgDealSize: number;
  }>;
}

export function useDashboard(dateRange: 'week' | 'month' | 'quarter' | 'year' = 'month') {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Oblicz zakres dat
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
      }

      // Poprzedni okres do porównania
      const prevEndDate = new Date(startDate);
      const prevStartDate = new Date(startDate);
      prevStartDate.setDate(prevStartDate.getDate() - (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

      // Pobierz dane
      const [
        offersResponse,
        clientsResponse,
        productsResponse,
        viewsResponse
      ] = await Promise.all([
        // Oferty z pozycjami
        supabase
          .from('offers')
          .select(`
            *,
            offer_items (*)
          `)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString()),
        
        // Klienci
        supabase
          .from('clients')
          .select('*'),
        
        // Produkty
        supabase
          .from('products')
          .select('*')
          .eq('is_active', true),
        
        // Statystyki wyświetleń
        supabase
          .from('offer_statistics')
          .select('*')
          .gte('viewed_at', startDate.toISOString())
      ]);

      // Poprzedni okres - oferty
      const { data: prevOffers } = await supabase
        .from('offers')
        .select('*')
        .gte('created_at', prevStartDate.toISOString())
        .lte('created_at', prevEndDate.toISOString());

      const offers = offersResponse.data || [];
      const clients = clientsResponse.data || [];
      const products = productsResponse.data || [];
      const views = viewsResponse.data || [];

      // Oblicz statystyki
      const acceptedOffers = offers.filter(o => o.status === 'accepted');
      const totalRevenue = acceptedOffers.reduce((sum, o) => sum + (o.total_net || 0), 0);
      const conversionRate = offers.length > 0 ? (acceptedOffers.length / offers.length) * 100 : 0;
      const averageOrderValue = acceptedOffers.length > 0 ? totalRevenue / acceptedOffers.length : 0;

      // Poprzedni okres
      const prevAcceptedOffers = prevOffers?.filter(o => o.status === 'accepted') || [];
      const prevRevenue = prevAcceptedOffers.reduce((sum, o) => sum + (o.total_net || 0), 0);
      const prevConversion = prevOffers && prevOffers.length > 0 
        ? (prevAcceptedOffers.length / prevOffers.length) * 100 
        : 0;

      // Oblicz zmiany procentowe
      const revenueChange = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;
      const offersChange = prevOffers && prevOffers.length > 0 
        ? ((offers.length - prevOffers.length) / prevOffers.length) * 100 
        : 0;
      const conversionChange = prevConversion > 0 
        ? conversionRate - prevConversion 
        : 0;

      // Przygotuj dane do wykresów
      const revenueData = prepareRevenueData(offers, dateRange);
      const productData = prepareProductData(offers);
      const statusData = prepareStatusData(offers);
      const topClients = await prepareTopClients(offers, clients);
      const recentActivities = await prepareRecentActivities(offers, clients, views);
      const salesPerformance = prepareSalesPerformance(offers);

      setData({
        stats: {
          totalRevenue,
          revenueChange,
          totalOffers: offers.length,
          offersChange,
          totalClients: clients.length,
          clientsChange: 0, // TODO: Oblicz na podstawie created_at klientów
          conversionRate,
          conversionChange,
          averageOrderValue,
          totalProducts: products.length
        },
        revenueData,
        productData,
        statusData,
        topClients,
        recentActivities,
        salesPerformance
      });

    } catch (err) {
      console.error('Dashboard error:', err);
      setError(err instanceof Error ? err.message : 'Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  // Funkcje pomocnicze
  const prepareRevenueData = (offers: any[], dateRange: string) => {
    const days = dateRange === 'week' ? 7 : dateRange === 'month' ? 30 : dateRange === 'quarter' ? 90 : 365;
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayOffers = offers.filter(o => 
        o.created_at.startsWith(dateStr)
      );
      
      const accepted = dayOffers.filter(o => o.status === 'accepted');
      const revenue = accepted.reduce((sum, o) => sum + (o.total_net || 0), 0);
      
      // Agreguj dane dla większych zakresów
      if (dateRange === 'year' && i % 30 !== 0) continue; // Co 30 dni dla roku
      if (dateRange === 'quarter' && i % 7 !== 0) continue; // Co tydzień dla kwartału
      
      data.push({
        date: date.toLocaleDateString('pl-PL', { 
          day: dateRange === 'week' || dateRange === 'month' ? 'numeric' : undefined,
          month: 'short',
          year: dateRange === 'year' ? 'numeric' : undefined
        }),
        revenue,
        offers: dayOffers.length,
        accepted: accepted.length
      });
    }
    
    return data;
  };

  const prepareProductData = (offers: any[]) => {
    const productMap = new Map<string, { value: number; count: number }>();
    
    // Pobierz pozycje ofert z relacji offer_items
    offers.forEach(offer => {
      // offer_items może być już załadowane przez Supabase jeśli użyto select z relacją
      if (offer.offer_items && Array.isArray(offer.offer_items)) {
        offer.offer_items.forEach((item: any) => {
          const key = item.product_name || 'Nieznany produkt';
          const current = productMap.get(key) || { value: 0, count: 0 };
          current.value += item.total_price || 0;
          current.count += item.quantity || 0;
          productMap.set(key, current);
        });
      }
      // Alternatywnie sprawdź czy items jest tablicą (stary format)
      else if (offer.items && Array.isArray(offer.items)) {
        offer.items.forEach((item: any) => {
          const key = item.productName || item.product_name || 'Nieznany produkt';
          const current = productMap.get(key) || { value: 0, count: 0 };
          current.value += item.totalPrice || item.total_price || 0;
          current.count += item.quantity || 0;
          productMap.set(key, current);
        });
      }
    });
    
    const total = Array.from(productMap.values()).reduce((sum, p) => sum + p.value, 0);
    
    const productArray = Array.from(productMap.entries())
      .map(([name, data]) => ({
        name,
        value: data.value,
        count: data.count,
        percentage: total > 0 ? (data.value / total) * 100 : 0
      }))
      .sort((a, b) => b.value - a.value);
    
    // Upewnij się, że zawsze zwracamy tablicę
    return productArray.length > 0 ? productArray.slice(0, 10) : [];
  };

  const prepareStatusData = (offers: any[]) => {
    const statusCounts = {
      draft: 0,
      sent: 0,
      accepted: 0,
      rejected: 0
    };
    
    offers.forEach(offer => {
      if (statusCounts.hasOwnProperty(offer.status)) {
        statusCounts[offer.status as keyof typeof statusCounts]++;
      }
    });
    
    const total = offers.length;
    
    return [
      { 
        name: 'Szkic', 
        value: statusCounts.draft, 
        color: '#6B7280',
        percentage: total > 0 ? (statusCounts.draft / total) * 100 : 0
      },
      { 
        name: 'Wysłane', 
        value: statusCounts.sent, 
        color: '#3B82F6',
        percentage: total > 0 ? (statusCounts.sent / total) * 100 : 0
      },
      { 
        name: 'Zaakceptowane', 
        value: statusCounts.accepted, 
        color: '#10B981',
        percentage: total > 0 ? (statusCounts.accepted / total) * 100 : 0
      },
      { 
        name: 'Odrzucone', 
        value: statusCounts.rejected, 
        color: '#EF4444',
        percentage: total > 0 ? (statusCounts.rejected / total) * 100 : 0
      }
    ];
  };

  const prepareTopClients = async (offers: any[], clients: any[]) => {
    const clientMap = new Map<string, {
      revenue: number;
      ordersCount: number;
      lastOrderDate: string;
      acceptedOrders: number;
    }>();
    
    offers.forEach(offer => {
      // Pobierz client_id zamiast zagnieżdżonego obiektu client
      const clientId = offer.client_id;
      if (clientId) {
        const current = clientMap.get(clientId) || {
          revenue: 0,
          ordersCount: 0,
          lastOrderDate: offer.created_at,
          acceptedOrders: 0
        };
        
        current.ordersCount++;
        if (offer.status === 'accepted') {
          current.revenue += offer.total_net_after_discount || offer.total_net || 0;
          current.acceptedOrders++;
        }
        
        if (new Date(offer.created_at) > new Date(current.lastOrderDate)) {
          current.lastOrderDate = offer.created_at;
        }
        
        clientMap.set(clientId, current);
      }
    });
    const clientArray = Array.from(clientMap.entries())
      .map(([clientId, data]) => {
        const client = clients.find(c => c.id === clientId);
        return {
          id: clientId,
          name: client?.name || 'Nieznany klient',
          revenue: data.revenue,
          ordersCount: data.ordersCount,
          lastOrderDate: data.lastOrderDate,
          growth: 0 // TODO: Oblicz rzeczywisty wzrost
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
    
    // Upewnij się, że zawsze zwracamy tablicę
    return clientArray.length > 0 ? clientArray.slice(0, 10) : [];
  };

  const prepareRecentActivities = async (offers: any[], clients: any[], views: any[]) => {
    const activities: any[] = [];
    
    // Nowe oferty
    (offers || []).slice(0, 5).forEach(offer => {
      activities.push({
        id: `offer-${offer.id}`,
        type: 'offer_created' as const,
        description: `Nowa oferta ${offer.offer_number || offer.number} dla ${offer.client?.name || 'klienta'}`,
        timestamp: offer.created_at,
        icon: 'FileText',
        color: 'text-blue-500'
      });
    });
    
    // Zaakceptowane oferty
    (offers || [])
      .filter(o => o.status === 'accepted')
      .slice(0, 3)
      .forEach(offer => {
        activities.push({
          id: `accepted-${offer.id}`,
          type: 'offer_accepted' as const,
          description: `Oferta ${offer.offer_number || offer.number} została zaakceptowana`,
          timestamp: offer.updated_at || offer.created_at,
          icon: 'CheckCircle',
          color: 'text-green-500'
        });
      });
    
    // Wyświetlenia ofert
    (views || [])
      .filter(v => v.user_agent?.startsWith('ACCEPTED:'))
      .slice(0, 3)
      .forEach(view => {
        activities.push({
          id: `view-${view.id}`,
          type: 'offer_viewed' as const,
          description: view.user_agent && typeof view.user_agent === 'string' && view.user_agent.startsWith('ACCEPTED:') 
            ? view.user_agent.substring(10) 
            : `Klient wyświetlił ofertę`,
          timestamp: view.viewed_at,
          icon: 'Eye',
          color: 'text-purple-500'
        });
      });
    
    // Sortuj po dacie
    activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    // Upewnij się, że zawsze zwracamy tablicę
    return activities.length > 0 ? activities.slice(0, 10) : [];
  };

  const prepareSalesPerformance = (offers: any[]) => {
    const salesMap = new Map<string, {
      revenue: number;
      offersCount: number;
      acceptedCount: number;
    }>();
    
    offers.forEach(offer => {
      const salesperson = offer.salesperson?.name || 'Nieprzypisany';
      const current = salesMap.get(salesperson) || {
        revenue: 0,
        offersCount: 0,
        acceptedCount: 0
      };
      
      current.offersCount++;
      if (offer.status === 'accepted') {
        current.revenue += offer.total_net || 0;
        current.acceptedCount++;
      }
      
      salesMap.set(salesperson, current);
    });
    
    return Array.from(salesMap.entries())
      .map(([salesperson, data]) => ({
        salesperson,
        revenue: data.revenue,
        offersCount: data.offersCount,
        conversionRate: data.offersCount > 0 ? (data.acceptedCount / data.offersCount) * 100 : 0,
        avgDealSize: data.acceptedCount > 0 ? data.revenue / data.acceptedCount : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  return {
    data,
    loading,
    error,
    refresh: fetchDashboardData
  };
}
